import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import CategoryCard from "./components/CategoryCard";
import CategoryModal from "./components/CategoryModal";
import GoalModal from "./components/GoalModal";
import { COLOR_OPTIONS } from "./components/constants";
import { openConfirmModal } from "@mantine/modals";
import css2 from "./../../../Insights/pages/MeetingTime/AsAttendee/AsAttendee.module.scss";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useOutletContext } from "react-router-dom";
import { useMutation } from "react-query";
import {
  createOrgGoalCategory,
  updateOrgGoalCategory,
  deleteOrgGoalCategory,
  addGoalToCategory,
  updateGoalInCategory,
  deleteGoalFromCategory,
} from "api";
import CategoryCardSkeleton from "./components/CategoryCardSkeleton";
import useGoalsQuery from "hooks/useGoalsQuery";

const CLOSED_CATEGORY_MODAL = {
  opened: false,
  mode: "create",
  categoryId: null,
  initial: null,
};

const CLOSED_GOAL_MODAL = {
  opened: false,
  mode: "create",
  categoryId: null,
  index: null,
  initial: null,
};

const ManageGoals = () => {
  const [orgDetails] = useOutletContext();

  const [query, setQuery] = useState("");
  const [parent] = useAutoAnimate();
  const [categoryModal, setCategoryModal] = useState(CLOSED_CATEGORY_MODAL);
  const [goalModal, setGoalModal] = useState(CLOSED_GOAL_MODAL);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [deletingGoalKey, setDeletingGoalKey] = useState(null);

  const { data, isLoading, isError, refetch } = useGoalsQuery(orgDetails?.id);

  // Goals are positional inside the category doc, so the array index is the
  // only handle we have for editing and deleting one. It is carried here rather
  // than recomputed in the card, which only ever sees the filtered list.
  const categories = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((doc) => ({
      id: doc.id,
      name: doc.name || "Untitled",
      description: doc.description || "",
      color: doc.color || "gray",
      goals: Array.isArray(doc.goals)
        ? doc.goals.map((goal, index) => ({
            id: `${doc.id}-g-${index}`,
            index,
            name: goal?.name || "",
            description: goal?.description || "",
          }))
        : [],
    }));
  }, [data]);

  const createCategoryMutation = useMutation(
    async (values) => createOrgGoalCategory(orgDetails?.id, values),
    { onSuccess: () => refetch() }
  );

  const updateCategoryMutation = useMutation(
    async ({ categoryId, values }) =>
      updateOrgGoalCategory(orgDetails?.id, categoryId, values),
    { onSuccess: () => refetch() }
  );

  const deleteCategoryMutation = useMutation(
    async (categoryId) => deleteOrgGoalCategory(orgDetails?.id, categoryId),
    { onSuccess: () => refetch() }
  );

  const saveGoalMutation = useMutation(
    async ({ categoryId, index, values }) =>
      index === null
        ? addGoalToCategory(orgDetails?.id, categoryId, values)
        : updateGoalInCategory(orgDetails?.id, categoryId, index, values),
    { onSuccess: () => refetch() }
  );

  const openCreateCategory = () => {
    setCategoryModal({
      opened: true,
      mode: "create",
      categoryId: null,
      initial: { name: "", description: "", color: COLOR_OPTIONS[0] },
    });
  };

  const openEditCategory = (category) => {
    setCategoryModal({
      opened: true,
      mode: "edit",
      categoryId: category.id,
      initial: {
        name: category.name,
        description: category.description,
        color: category.color,
      },
    });
  };

  const handleSubmitCategory = async (values) => {
    try {
      if (categoryModal.mode === "create") {
        await createCategoryMutation.mutateAsync({
          name: values.name,
          description: values.description,
          color: values.color,
          goals: [],
        });
      } else if (categoryModal.categoryId) {
        await updateCategoryMutation.mutateAsync({
          categoryId: categoryModal.categoryId,
          values: {
            name: values.name,
            description: values.description,
            color: values.color,
          },
        });
      }
      setCategoryModal(CLOSED_CATEGORY_MODAL);
    } catch (e) {
      // The modal stays open so the admin can retry without retyping.
      console.error("Failed to save category", e);
    }
  };

  const handleDeleteCategory = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    openConfirmModal({
      title: "Delete category",
      children: (
        <Text size="sm">
          Are you sure you want to delete <b>{category?.name}</b> and all its
          goals?
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          setDeletingCategoryId(categoryId);
          await deleteCategoryMutation.mutateAsync(categoryId);
        } finally {
          setDeletingCategoryId(null);
        }
      },
    });
  };

  const openCreateGoal = (categoryId) => {
    setGoalModal({
      opened: true,
      mode: "create",
      categoryId,
      index: null,
      initial: { name: "", description: "" },
    });
  };

  const openEditGoal = (categoryId, goal) => {
    setGoalModal({
      opened: true,
      mode: "edit",
      categoryId,
      index: goal.index,
      initial: { name: goal.name, description: goal.description },
    });
  };

  const handleSubmitGoal = async (values) => {
    try {
      await saveGoalMutation.mutateAsync({
        categoryId: goalModal.categoryId,
        index: goalModal.index,
        values,
      });
      setGoalModal(CLOSED_GOAL_MODAL);
    } catch (e) {
      console.error("Failed to save goal", e);
    }
  };

  const handleDeleteGoal = (categoryId, goal) => {
    openConfirmModal({
      title: "Delete goal",
      children: <Text size="sm">Delete “{goal.name}”?</Text>,
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          setDeletingGoalKey(goal.id);
          await deleteGoalFromCategory(orgDetails?.id, categoryId, goal.index);
          await refetch();
        } finally {
          setDeletingGoalKey(null);
        }
      },
    });
  };

  const matchesQuery = (value) =>
    String(value || "")
      .toLowerCase()
      .includes(query.toLowerCase());

  return (
    <div className={css2.container}>
      <Stack w={"100%"} py={30}>
        <Flex justify="space-between" align="center" gap="sm" w={"100%"}>
          <Title order={3}>Manage Goals</Title>
          <Group spacing="sm" noWrap>
            <TextInput
              placeholder="Search goals"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
            <Button
              leftIcon={<IconPlus size={18} />}
              onClick={openCreateCategory}
              variant="filled"
            >
              Add Category
            </Button>
          </Group>
        </Flex>

        {isLoading ? (
          <SimpleGrid
            cols={2}
            spacing="lg"
            mt={20}
            breakpoints={[{ maxWidth: 1300, cols: 1, spacing: "md" }]}
          >
            {Array.from({ length: 4 }).map((_, idx) => (
              <CategoryCardSkeleton key={idx} />
            ))}
          </SimpleGrid>
        ) : isError ? (
          <Box c="red">
            <Flex justify="space-between" align="center" w={"100%"}>
              <Text>Error loading categories.</Text>
              <Button variant="light" onClick={() => refetch()}>
                Retry
              </Button>
            </Flex>
          </Box>
        ) : (
          <SimpleGrid
            cols={2}
            spacing="lg"
            mt={20}
            breakpoints={[{ maxWidth: 1300, cols: 1, spacing: "md" }]}
            ref={parent}
          >
            {categories.map((category) => {
              const visibleGoals = query
                ? category.goals.filter(
                    (g) => matchesQuery(g.name) || matchesQuery(g.description)
                  )
                : category.goals;
              return (
                <CategoryCard
                  key={category.id}
                  category={{ ...category, goals: visibleGoals }}
                  goalCount={category.goals.length}
                  onAddGoal={() => openCreateGoal(category.id)}
                  onEditCategory={() => openEditCategory(category)}
                  onDeleteCategory={() => handleDeleteCategory(category.id)}
                  isDeleting={deletingCategoryId === category.id}
                  onEditGoal={(goal) => openEditGoal(category.id, goal)}
                  onDeleteGoal={(goal) => handleDeleteGoal(category.id, goal)}
                  deletingGoalId={deletingGoalKey}
                />
              );
            })}
            {categories.length === 0 && (
              <Box c="dimmed" ta="center" py="lg">
                No categories yet. Click "Add Category" to create one.
              </Box>
            )}
          </SimpleGrid>
        )}
      </Stack>

      <CategoryModal
        opened={categoryModal.opened}
        initialValues={categoryModal.initial}
        mode={categoryModal.mode}
        onClose={() => setCategoryModal(CLOSED_CATEGORY_MODAL)}
        onSubmit={handleSubmitCategory}
        isSaving={
          createCategoryMutation.isLoading || updateCategoryMutation.isLoading
        }
      />

      <GoalModal
        opened={goalModal.opened}
        initialValues={goalModal.initial}
        mode={goalModal.mode}
        onClose={() => setGoalModal(CLOSED_GOAL_MODAL)}
        onSubmit={handleSubmitGoal}
        isSaving={saveGoalMutation.isLoading}
      />
    </div>
  );
};

export default ManageGoals;
